import Phaser from 'phaser';
import { getRankProgress } from '@/data/ranks';
import { getActiveStudent, setActiveStudent } from '@/systems/ActiveStudent';
import { signOut } from '@/systems/AuthService';
import { isDevModeEnabled, setDevModeEnabled, tryUnlockWithPassword } from '@/systems/DevMode';
import { isCurrentUserAdmin } from '@/systems/ParentService';
import { getTopStudents, type RankingEntry } from '@/systems/RankingService';
import { saveSystem } from '@/systems/SaveSystem';
import { listStudentsWithProgress, type StudentWithProgress } from '@/systems/StudentService';
import { addBackdrop } from '@/ui/background';
import { Button } from '@/ui/components/Button';
import { MuteToggle } from '@/ui/components/MuteToggle';
import { Palette, Typography } from '@/ui/theme';
import { fadeInScene, fadeToScene } from '@/utils/transitions';

/**
 * MenuScene — porta de entrada após a splash. Mostra perfil resumido
 * (nome, rank, barra de XP) e CTA principal.
 *
 * Outras opções (Treino Livre, Ranking, Configurações) virão em
 * iterações seguintes — ver CLAUDE.md §4.2 e roadmap.
 */
export class MenuScene extends Phaser.Scene {
  constructor() {
    super({ key: 'MenuScene' });
  }

  create(): void {
    addBackdrop(this);
    fadeInScene(this);
    const { width, height } = this.scale;

    this.add
      .text(width / 2, height * 0.18, 'Tabuada Geek', {
        fontFamily: Typography.fontFamily,
        fontSize: '88px',
        color: Palette.textPrimaryCss,
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    this.add
      .text(width / 2, height * 0.18 + 70, 'Vire um Mestre Geek da multiplicação', {
        fontFamily: Typography.fontFamily,
        fontSize: '24px',
        color: Palette.textMutedCss,
      })
      .setOrigin(0.5);

    this.renderProfileCard();

    new Button(this, width / 2, height * 0.65, 'Jogar', {
      width: 280,
      height: 72,
      fontSize: 32,
      onClick: () => this.handlePlay(),
    });

    new Button(this, width / 2 - 150, height * 0.65 + 92, 'Treino Livre', {
      width: 270,
      height: 56,
      fontSize: 20,
      fillColor: 0x2a2a4a,
      hoverColor: Palette.accent,
      onClick: () => fadeToScene(this, 'TrainingPickScene'),
    });

    new Button(this, width / 2 + 150, height * 0.65 + 92, 'Contra o Tempo', {
      width: 270,
      height: 56,
      fontSize: 20,
      fillColor: 0x2a2a4a,
      hoverColor: Palette.warning,
      onClick: () => fadeToScene(this, 'TimeAttackScene'),
    });

    this.add
      .text(width / 2, height - 32, 'v0.1.0 — em desenvolvimento', {
        fontFamily: Typography.fontFamily,
        fontSize: '14px',
        color: Palette.textDimCss,
      })
      .setOrigin(0.5);

    new MuteToggle(this, width - 48, 48);
    this.renderSignOutButton();
    this.renderAdminAffordance();
    void this.renderStudentsSidebar();
    void this.renderRankingSidebar();
    void this.renderAdminBadge();
  }

  /**
   * Sidebar à direita com o Top 20 público (apelido + XP + posição).
   * Highlight do aluno ativo se ele estiver no top — empurra a criança
   * a melhorar de posição. Pseudonimato é garantido pela função SQL
   * top_students (CLAUDE.md §15).
   */
  private async renderRankingSidebar(): Promise<void> {
    const cardWidth = 220;
    const x = this.scale.width - 24 - cardWidth;
    const titleY = 180;

    const title = this.add
      .text(x + cardWidth / 2, titleY, '🏆 Top 20', {
        fontFamily: Typography.fontFamily,
        fontSize: '20px',
        color: Palette.warningCss,
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    const placeholder = this.add
      .text(x + cardWidth / 2, titleY + 40, 'Carregando ranking...', {
        fontFamily: Typography.fontFamily,
        fontSize: '13px',
        color: Palette.textDimCss,
      })
      .setOrigin(0.5);

    const result = await getTopStudents(20);
    placeholder.destroy();

    if (!result.ok) {
      this.add
        .text(x + cardWidth / 2, titleY + 40, `Erro: ${result.error}`, {
          fontFamily: Typography.fontFamily,
          fontSize: '12px',
          color: Palette.dangerCss,
          wordWrap: { width: cardWidth },
          align: 'center',
        })
        .setOrigin(0.5, 0);
      return;
    }

    if (result.value.length === 0) {
      title.destroy();
      this.add
        .text(x + cardWidth / 2, titleY, 'Ninguém no ranking ainda — seja o primeiro!', {
          fontFamily: Typography.fontFamily,
          fontSize: '13px',
          color: Palette.textDimCss,
          wordWrap: { width: cardWidth },
          align: 'center',
        })
        .setOrigin(0.5, 0);
      return;
    }

    this.renderRankingRows(x, titleY + 28, cardWidth, result.value);
  }

  private renderRankingRows(x: number, startY: number, cardWidth: number, entries: RankingEntry[]): void {
    const rowHeight = 22;
    const active = getActiveStudent();
    const activeName = active?.displayName;

    entries.forEach((entry, idx) => {
      const y = startY + idx * rowHeight;
      const isMe = entry.displayName === activeName;
      const color = isMe ? Palette.warningCss : Palette.textPrimaryCss;
      const positionColor = isMe ? Palette.warningCss : Palette.textMutedCss;

      // Posição (#1, #2, ...) à esquerda
      this.add
        .text(x + 4, y, `#${entry.position}`, {
          fontFamily: Typography.fontFamily,
          fontSize: '13px',
          color: positionColor,
          fontStyle: 'bold',
        });

      // Apelido no meio (truncado se muito longo)
      this.add
        .text(x + 44, y, entry.displayName, {
          fontFamily: Typography.fontFamily,
          fontSize: '13px',
          color,
          fontStyle: isMe ? 'bold' : 'normal',
        })
        .setStyle({ wordWrap: { width: cardWidth - 100 } });

      // XP à direita
      this.add
        .text(x + cardWidth - 4, y, `${entry.totalXP} XP`, {
          fontFamily: Typography.fontFamily,
          fontSize: '13px',
          color: positionColor,
        })
        .setOrigin(1, 0);
    });
  }

  /**
   * Atalho clicável "⚙️ Painel Admin" no topo central quando o pai
   * logado tem is_admin=true. Confirma o flag visualmente E leva pra
   * AdminPanelScene (lista global de alunos).
   */
  private async renderAdminBadge(): Promise<void> {
    const isAdmin = await isCurrentUserAdmin();
    if (!isAdmin) return;
    const link = this.add
      .text(this.scale.width / 2, 152, '⚙️ Painel Admin', {
        fontFamily: Typography.fontFamily,
        fontSize: '15px',
        color: Palette.warningCss,
        fontStyle: 'bold',
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
    link.on(Phaser.Input.Events.POINTER_OVER, () => link.setColor(Palette.textPrimaryCss));
    link.on(Phaser.Input.Events.POINTER_OUT, () => link.setColor(Palette.warningCss));
    link.on(Phaser.Input.Events.POINTER_UP, () => fadeToScene(this, 'AdminPanelScene'));
  }

  /**
   * Sidebar à esquerda listando todos os alunos do pai com o XP de cada
   * um. Click em outro aluno troca de perfil em 1 clique (sem precisar
   * passar pelo ProfileSelectScene). Carregamento async — placeholder
   * leve enquanto a query Supabase termina.
   */
  private async renderStudentsSidebar(): Promise<void> {
    const x = 24;
    const titleY = 180;
    const cardWidth = 200;

    const title = this.add
      .text(x + cardWidth / 2, titleY, 'Alunos', {
        fontFamily: Typography.fontFamily,
        fontSize: '20px',
        color: Palette.textMutedCss,
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    const placeholder = this.add
      .text(x + cardWidth / 2, titleY + 40, 'Carregando...', {
        fontFamily: Typography.fontFamily,
        fontSize: '14px',
        color: Palette.textDimCss,
      })
      .setOrigin(0.5);

    const result = await listStudentsWithProgress();
    placeholder.destroy();

    if (!result.ok) {
      this.add
        .text(x + cardWidth / 2, titleY + 40, `Erro: ${result.error}`, {
          fontFamily: Typography.fontFamily,
          fontSize: '12px',
          color: Palette.dangerCss,
          wordWrap: { width: cardWidth },
          align: 'center',
        })
        .setOrigin(0.5, 0);
      return;
    }

    if (result.value.length === 0) {
      title.destroy();
      return; // sem alunos não há sidebar — usuário está no menu sem aluno ativo, edge case
    }

    this.renderStudentCards(x, titleY + 30, cardWidth, result.value);
  }

  private renderStudentCards(x: number, startY: number, cardWidth: number, students: StudentWithProgress[]): void {
    const cardHeight = 64;
    const gap = 10;
    const active = getActiveStudent();

    students.forEach((student, idx) => {
      const y = startY + idx * (cardHeight + gap);
      const isActive = student.id === active?.id;

      const fill = isActive ? Palette.primary : 0x1f1f3a;
      const bg = this.add.graphics();
      const drawCard = (hover: boolean) => {
        bg.clear();
        bg.fillStyle(fill, 1);
        bg.fillRoundedRect(x, y, cardWidth, cardHeight, 10);
        if (isActive) {
          bg.lineStyle(2, Palette.accent, 1);
          bg.strokeRoundedRect(x, y, cardWidth, cardHeight, 10);
        } else if (hover) {
          bg.lineStyle(2, Palette.textMuted, 1);
          bg.strokeRoundedRect(x, y, cardWidth, cardHeight, 10);
        }
      };
      drawCard(false);

      this.add.text(x + 12, y + 10, student.displayName, {
        fontFamily: Typography.fontFamily,
        fontSize: '15px',
        color: Palette.textPrimaryCss,
        fontStyle: 'bold',
        wordWrap: { width: cardWidth - 24 },
      });

      this.add.text(x + 12, y + 36, `${student.totalXP} XP`, {
        fontFamily: Typography.fontFamily,
        fontSize: '13px',
        color: isActive ? Palette.textPrimaryCss : Palette.textMutedCss,
      });

      // Aluno ativo não é clicável (já é ele). Os demais trocam de perfil
      // em 1 clique — bind no save + restart da scene pra refletir XP/rank.
      if (!isActive) {
        const hit = this.add
          .rectangle(x + cardWidth / 2, y + cardHeight / 2, cardWidth, cardHeight, 0xffffff, 0)
          .setInteractive({ useHandCursor: true });
        hit.on(Phaser.Input.Events.POINTER_OVER, () => drawCard(true));
        hit.on(Phaser.Input.Events.POINTER_OUT, () => drawCard(false));
        hit.on(Phaser.Input.Events.POINTER_UP, () => {
          void this.switchToStudent(student);
        });
      }
    });

    // Atalho discreto pra ProfileSelectScene (gerenciar alunos: criar/excluir).
    const manageY = startY + students.length * (cardHeight + gap) + 6;
    const manage = this.add
      .text(x + cardWidth / 2, manageY, '+ Gerenciar alunos', {
        fontFamily: Typography.fontFamily,
        fontSize: '13px',
        color: Palette.textDimCss,
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
    manage.on(Phaser.Input.Events.POINTER_OVER, () => manage.setColor(Palette.textPrimaryCss));
    manage.on(Phaser.Input.Events.POINTER_OUT, () => manage.setColor(Palette.textDimCss));
    manage.on(Phaser.Input.Events.POINTER_UP, () => fadeToScene(this, 'ProfileSelectScene'));
  }

  private async switchToStudent(student: StudentWithProgress): Promise<void> {
    const stripped = { id: student.id, displayName: student.displayName, createdAt: student.createdAt };
    setActiveStudent(stripped);
    try {
      await saveSystem.bindToStudent(student.id);
    } catch (err) {
      console.error('[MenuScene] switchToStudent falhou', err);
      return;
    }
    this.scene.restart();
  }

  /**
   * Botão "Sair" pequeno no canto superior direito (ao lado do mute).
   * Chama signOut e leva pra AuthScene. Erros silenciosos — botão fica
   * desabilitado durante a chamada pra evitar duplo clique.
   */
  private renderSignOutButton(): void {
    const { width } = this.scale;
    const button = this.add
      .text(width - 110, 48, 'Sair', {
        fontFamily: Typography.fontFamily,
        fontSize: '16px',
        color: Palette.textMutedCss,
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true });
    button.on(Phaser.Input.Events.POINTER_OVER, () => button.setColor(Palette.textPrimaryCss));
    button.on(Phaser.Input.Events.POINTER_OUT, () => button.setColor(Palette.textMutedCss));
    button.on(Phaser.Input.Events.POINTER_UP, async () => {
      button.setAlpha(0.5);
      const result = await signOut();
      if (!result.ok) {
        console.error('[MenuScene] signOut falhou', result.error);
        button.setAlpha(1);
        return;
      }
      // Tira o aluno ativo junto: signOut sem isso deixaria a próxima
      // entrada do mesmo navegador pular o ProfileSelect indevidamente.
      setActiveStudent(null);
      // DevMode é persistido em localStorage; sem isso ele "vaza" pra
      // próxima conta criada no mesmo navegador e destrava mundos sem
      // progresso real. Limpar no signOut deixa a regra pedagógica
      // valer por padrão pra cada nova sessão.
      setDevModeEnabled(false);
      fadeToScene(this, 'AuthScene');
    });

    // Indicador "Jogando como X" + botão pra trocar de aluno.
    const active = getActiveStudent();
    if (active) {
      const trocar = this.add
        .text(width - 110, 78, `🎮 ${active.displayName} · trocar`, {
          fontFamily: Typography.fontFamily,
          fontSize: '13px',
          color: Palette.textDimCss,
        })
        .setOrigin(0.5)
        .setInteractive({ useHandCursor: true });
      trocar.on(Phaser.Input.Events.POINTER_OVER, () => trocar.setColor(Palette.textPrimaryCss));
      trocar.on(Phaser.Input.Events.POINTER_OUT, () => trocar.setColor(Palette.textDimCss));
      trocar.on(Phaser.Input.Events.POINTER_UP, () => fadeToScene(this, 'ProfileSelectScene'));
    }
  }

  /**
   * Botão "🔧" discreto no canto inferior esquerdo + badge quando o modo
   * dev estiver ativo. Pedido pelo dono pra testar fases sem grindar
   * estrelas (CLAUDE.md §14: tom de incentivo, dev tools internas tudo
   * bem desde que não exposta como progressão regular pra criança).
   */
  private renderAdminAffordance(): void {
    const { height } = this.scale;

    const button = this.add
      .text(24, height - 28, '🔧', {
        fontFamily: Typography.fontFamily,
        fontSize: '20px',
        color: Palette.textDimCss,
      })
      .setOrigin(0, 0.5)
      .setInteractive({ useHandCursor: true });
    button.on(Phaser.Input.Events.POINTER_OVER, () => button.setColor(Palette.textPrimaryCss));
    button.on(Phaser.Input.Events.POINTER_OUT, () => button.setColor(Palette.textDimCss));
    button.on(Phaser.Input.Events.POINTER_UP, () => this.handleAdminClick());

    if (isDevModeEnabled()) this.renderDevBadge();
  }

  private renderDevBadge(): void {
    const { width, height } = this.scale;
    this.add
      .text(width / 2, height - 56, '🔓 Modo Dev ativo — todas as fases destravadas', {
        fontFamily: Typography.fontFamily,
        fontSize: '14px',
        color: Palette.warningCss,
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
  }

  private handleAdminClick(): void {
    if (isDevModeEnabled()) {
      const turnOff = window.confirm('Modo Dev está ativo. Desativar?');
      if (turnOff) {
        setDevModeEnabled(false);
        this.scene.restart();
      }
      return;
    }

    const input = window.prompt('Senha admin:');
    if (input === null) return;
    if (tryUnlockWithPassword(input)) {
      window.alert('Modo Dev ativado — todas as fases destravadas.');
      this.scene.restart();
    } else {
      window.alert('Senha incorreta.');
    }
  }

  private renderProfileCard(): void {
    const { width, height } = this.scale;
    const data = saveSystem.getData();
    const progress = getRankProgress(data.totalXP);
    const cardY = height * 0.42;

    this.add
      .text(width / 2, cardY, data.playerName, {
        fontFamily: Typography.fontFamily,
        fontSize: '28px',
        color: Palette.textPrimaryCss,
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    this.add
      .text(width / 2, cardY + 36, progress.current.name, {
        fontFamily: Typography.fontFamily,
        fontSize: '22px',
        color: progress.current.accentCss,
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    this.renderXpBar(cardY + 78, progress);
  }

  private renderXpBar(y: number, progress: ReturnType<typeof getRankProgress>): void {
    const { width } = this.scale;
    const barWidth = 400;
    const barHeight = 12;
    const barX = width / 2 - barWidth / 2;

    const frame = this.add.graphics();
    frame.lineStyle(2, Palette.textMuted, 1);
    frame.strokeRoundedRect(barX - 2, y - 2, barWidth + 4, barHeight + 4, 6);

    const fill = this.add.graphics();
    fill.fillStyle(Palette.accent, 1);
    fill.fillRoundedRect(barX, y, barWidth * progress.ratio, barHeight, 6);

    const xpText = progress.next
      ? `${progress.xpIntoCurrent} / ${progress.xpToNext} XP — próximo: ${progress.next.name}`
      : `${progress.xpIntoCurrent} XP — rank máximo!`;

    this.add
      .text(this.scale.width / 2, y + barHeight + 16, xpText, {
        fontFamily: Typography.fontFamily,
        fontSize: '16px',
        color: Palette.textDimCss,
      })
      .setOrigin(0.5, 0);
  }

  private handlePlay(): void {
    fadeToScene(this, 'WorldMapScene');
  }
}
